import React, { useCallback, useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  CheckboxList,
  DialogOverlayActions,
  Ellipsis,
  ListItem,
  Skeleton,
  TransitioningDialogOverlay,
  type useOverlay,
} from '@fs/zion-ui'
import { Route, useHistory } from '@fs/zion-router'
import { ContentAdd } from '@fs/zion-icon'
import playlistsConfig from '../../../../config/feature/playlist'
import PillButton from '../../../../lib/buttons/PillButton'
import UpdatePlaylistForm from '../UpdatePlaylistForm'
import { usePlaylists } from '../../../../hooks/playlist/usePlaylists'
import { usePlaylistCreation } from '../../../../hooks/playlist/usePlaylistCreation'
import { usePlaylistsByItems } from '../../../../hooks/playlist/usePlaylistsByItems'
import { usePlaylistUpdate } from '../../../../hooks/playlist/usePlaylistUpdate'

const Loading = ({ count = 3 }): JSX.Element => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Skeleton.Checkbox key={index} />
    ))}
  </>
)

const UpdatePlaylist = ({
  sessionId,
  onPlaylistUpdated,
}: {
  sessionId: string
  onPlaylistUpdated: (selectedIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { playlists, isFetching: isPlaylistsLoading } = usePlaylists()
  const { playlistsByItems, isLoading, isFetching } = usePlaylistsByItems([sessionId])
  const { updatePlaylist, isPending: isUpdating } = usePlaylistUpdate()
  const [pendingId, setPendingId] = useState('')
  const queryClient = useQueryClient()

  const onChange = useCallback(
    async (selectedIds, lastTouched: { name: string; checked: boolean }): Promise<void> => {
      const { name: playlistId, checked } = lastTouched
      setPendingId(playlistId)
      if (checked) {
        await updatePlaylist({ id: playlistId, addItems: [sessionId] })
      } else {
        await updatePlaylist({ id: playlistId, removeItems: [sessionId] })
      }
      onPlaylistUpdated(selectedIds)
    },
    [updatePlaylist, sessionId, onPlaylistUpdated]
  )

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['playlistsByItems'] })
    }
  }, [queryClient])

  if (isLoading || isFetching) {
    return <Loading count={playlists.length} />
  }

  const initialSelection = playlistsByItems[sessionId]?.results.map(({ id }) => id)

  return (
    <CheckboxList onSelectionChange={onChange} initialSelection={initialSelection}>
      {playlists
        .filter((p) => !p.parent)
        .map((playlist) => {
          const full = !playlist.default && playlist.size >= playlistsConfig.sessionLimit

          let secondaryText = t('playlists.playlist.size', '{ playlistSize } videos', {
            playlistSize: playlist.size || 0,
          })
          if (full) {
            secondaryText = t('playlists.playlists.size-status', '{ playlistSize } videos (FULL)', {
              playlistSize: playlist.size,
            })
          }

          const isPending = (isUpdating || isPlaylistsLoading) && pendingId === playlist.id

          return (
            <ListItem
              data-testid={`${playlist.default ? 'watch-later' : playlist.name}-playlist`}
              dense
              disabled={full || isPending}
              primaryText={
                <Ellipsis>
                  <strong>{playlist.name}</strong>
                </Ellipsis>
              }
              secondaryText={secondaryText}
              key={playlist.id}
              name={playlist.id}
              linkName={`Add to ${playlist.default ? 'Default' : 'Other'} Playlist`}
            />
          )
        })}
    </CheckboxList>
  )
}

const CreatePlaylist = ({ sessionId }): JSX.Element => {
  const history = useHistory()
  const { createPlaylist } = usePlaylistCreation()

  const onSubmit = async ({ name, description }): Promise<void> => {
    await createPlaylist({ name, description, initialItem: sessionId || undefined })
    history.goBack()
  }

  return <UpdatePlaylistForm onSubmit={onSubmit} />
}

export const PlaylistManagementOverlay = ({
  sessionId,
  overlay,
  onPlaylistUpdated,
}: {
  sessionId: string
  overlay: ReturnType<typeof useOverlay>
  onPlaylistUpdated: (selectedIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { playlists, isLoading } = usePlaylists()

  const canCreatePlaylists = playlists.filter((p) => !p.parent).length < playlistsConfig.playlistLimit

  return (
    <TransitioningDialogOverlay
      {...overlay}
      size="sm"
      title={t('playlists.overlay.heading', 'Add to...')}
      initialLocation="/update"
    >
      <Route
        path="/update"
        footer={
          <DialogOverlayActions>
            <PillButton
              Icon={ContentAdd}
              color="blue"
              disabled={!canCreatePlaylists}
              emphasis="low"
              linkName="Create Playlist"
              data-testid="create-playlist"
              to="/create"
            >
              {t('playlists.collection.create.action', 'Create Playlist')}
            </PillButton>
          </DialogOverlayActions>
        }
      >
        {isLoading ? <Loading /> : <UpdatePlaylist sessionId={sessionId} onPlaylistUpdated={onPlaylistUpdated} />}
      </Route>
      <Route path="/create" title={t('playlists.create.title', 'Create New Playlist')}>
        <CreatePlaylist sessionId={sessionId} />
      </Route>
    </TransitioningDialogOverlay>
  )
}
